export const paperUrl = "https://arxiv.org/abs/2603.29761";

export const aboutSections = {
  sectionIds: [
    "overview",
    "maia",
    "bottleneck",
    "results",
    "risks",
    "sft",
    "future",
    "story",
    "developers",
  ],
  hero: {
    eyebrow: "关于 Catie",
    title: "Catie 像语言一样预测国际象棋。",
    body:
      "Catie 会把整盘棋读成一个走法 token 序列，然后直接预测下一步。不用 Stockfish，不展开搜索树，也不在界面背后隐藏引擎变化。",
  },
} as const;

export const aboutNavigation = [
  { id: "overview", label: "概览" },
  { id: "maia", label: "Maia" },
  { id: "bottleneck", label: "追踪" },
  { id: "results", label: "结果" },
  { id: "risks", label: "边界" },
  { id: "sft", label: "SFT" },
  { id: "future", label: "未来" },
  { id: "story", label: "故事" },
  { id: "developers", label: "团队" },
] as const;

export const aboutHeroSignals = [
  "整盘棋历史是输入",
  "下一步棋是 token",
  "拟人行为是研究对象",
] as const;

export const aboutHeroTokens = ["e2e4", "c7c5", "g1f3", "d7d6"] as const;

export const aboutPredictionBars = [
  { move: "d2d4", label: "打开中心", value: 84 },
  { move: "f1b5", label: "施加牵制", value: 58 },
  { move: "b1c3", label: "自然出子", value: 42 },
] as const;

export const aboutFigureChecks = [
  { value: "0", label: "引擎节点" },
  { value: "0", label: "搜索变化" },
  { value: "1", label: "下一个 token" },
] as const;

export const aboutMetrics = [
  {
    value: "2570",
    label: "Lichess 子弹棋",
    detail: "最终 120M 无搜索模型，在 253 盘有评级对局上的结果。",
  },
  {
    value: "55.2%",
    label: "Top-1 人类走法预测",
    detail: "原生输入评测；更完整的棋谱序列上下文就是优势的一部分。",
  },
  {
    value: "0.26%",
    label: "非法走法率",
    detail: "这是判断 Catie 是否仍然“记得棋盘”的实用追踪信号。",
  },
  {
    value: "120M",
    label: "最终模型规模",
    detail: "规模提升修复了足够多的棋盘追踪问题，才让更强决策学习变得可行。",
  },
] as const;

export const maiaComparison = [
  {
    title: "Maia 风格模型",
    label: "先看局面",
    lines: ["读取一个棋盘局面。", "按 rating band 预测人类走法。", "是很强的静态人类走法基线。"],
  },
  {
    title: "Catie",
    label: "先读整盘棋",
    lines: [
      "读取完整走法历史。",
      "把下一步棋当成 token 预测。",
      "能保留风格、惯性和重复局面的上下文。",
    ],
  },
] as const;

export const bottleneckCards = [
  {
    key: "T",
    title: "追踪能力",
    body: "Catie 必须从走法历史里还原棋盘。如果追踪失败，它可能会尝试移动一枚已经不在那里的棋子。",
  },
  {
    key: "Q",
    title: "决策质量",
    body: "Catie 还必须走出符合目标棋手风格、同时看起来合理且有强度的棋。",
  },
  {
    key: "P",
    title: "实战强度",
    body: "论文把实战表现描述为受短板限制：P <= min(T, Q)。",
  },
] as const;

export const capabilityBars = [
  {
    label: "追踪能力",
    value: 92,
    detail: "模型能不能从走法历史里重建棋盘？",
  },
  {
    label: "决策质量",
    value: 78,
    detail: "它能不能为目标风格选择合理且有强度的一步？",
  },
  {
    label: "实战表现",
    value: 78,
    detail: "强度会被更弱的能力限制住。",
  },
] as const;

export const issueCards = [
  {
    title: "子力失误风险",
    body: "我们承认模型目前仍然会犯严重失误，原因包括：a) 不做计算；b) 基础模型数据使用了大量 bullet 对局。我们正在积极寻找改进方式，后续会继续更新。",
  },
  {
    title: "后半盘断崖",
    body: "有些对局会在某个转换点之后更容易出现严重错误。最终模型把这个点推得更晚，也让它更少发生，但这仍然是真实的研究边界。",
  },
  {
    title: "过度偏向强棋会崩",
    body: "只朝高质量走法训练，可能会悄悄损害棋盘追踪。更低的验证损失不一定意味着更强的实战表现。",
  },
] as const;

export const sftNotes = [
  {
    title: "一个基础模型",
    body: "Catie 从一个广泛的序列模型开始，训练数据覆盖大量 rating、局面和走法 token。",
  },
  {
    title: "许多微调棋手",
    body: "SFT 可以把这个基础模型适配成不同风格：激进、稳健、像初学者、像大师，或者像某个具体棋手。",
  },
  {
    title: "容量很重要",
    body: "早期 28M 的 SFT 尝试会削弱模型。到了 120M 基础模型上，Hikaru 风格微调才出现了有意义的增益。",
  },
] as const;

export const futureItems = [
  "用更好的数据平衡改进基础模型，包括更强的 blitz 覆盖。",
  "把 Catie 拆成更清楚的模型家族：风格、强度、个性分别表达。",
  "加入校准探针，展示模型的走法偏好和可能的胜和负倾向。",
  "以后用探针信号，把模型决策解释成自然语言。",
] as const;

export const devStory = [
  {
    step: "01",
    title: "基线能下棋，但会漂移",
    body: "第一个序列模型已经能下棋，但越往后走，对局质量越容易下降。",
  },
  {
    step: "02",
    title: "过滤看起来合理，但失败了",
    body: "只保留高 Elo 对局会让开局变好，却损害中局和残局追踪。",
  },
  {
    step: "03",
    title: "扩大规模修复追踪",
    body: "从 28M 扩到 120M 参数后，非法走法显著减少。",
  },
  {
    step: "04",
    title: "Elo 加权找到了平衡",
    body: "线性加权保留了低 Elo 数据的复杂多样性，同时让强棋谱有更高影响力。",
  },
] as const;

export const aboutDevelopers = [
  {
    name: "Fox Jiang",
    affiliation: "独立研究者",
    role: "项目负责人 / 首席开发",
    initials: "FJ",
  },
  {
    name: "GaGa Long",
    affiliation: "Abbey Park High School",
    role: "网页开发 / 模型开发",
    initials: "GL",
  },
  {
    name: "William Jiang",
    affiliation: "Shanghai Soong Ching Ling School",
    role: "模型开发",
    initials: "WJ",
  },
] as const;
